// src/components/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import zipcodes from 'zipcodes'; // validates real U.S. ZIPs
import Header from './Header';
import './LandingPage.css';

function LandingPage() {
  const [zipCode, setZipCode] = useState('');
  const [query, setQuery] = useState(''); // free-text userQuery
  const [zipError, setZipError] = useState('');
  const [suggestions, setSuggestions] = useState([]); // keyword suggestions
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1); // keyboard focus

  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // real 5-digit U.S. ZIPs only
  const isValidZip = (z) => /^\d{5}$/.test(z) && !!zipcodes.lookup(z);

  useEffect(() => {
    const zipFromUrl = searchParams.get('zip');
    const qFromUrl = searchParams.get('q');
    const { zipCode: zipFromState, userQuery: qFromState } = location.state || {};

    if (zipFromUrl && isValidZip(zipFromUrl)) {
      setZipCode(zipFromUrl);
    } else if (zipFromState && isValidZip(zipFromState)) {
      setZipCode(zipFromState);
    }

    if (qFromUrl) {
      setQuery(qFromUrl);
    } else if (qFromState) {
      setQuery(qFromState);
    }

    if (((zipFromUrl && qFromUrl && isValidZip(zipFromUrl)) || (zipFromState && qFromState && isValidZip(zipFromState)))) {
      const zip = zipFromUrl || zipFromState;
      const q = qFromUrl || qFromState;
      navigate('/results', { state: { zipCode: zip, userQuery: q } });
    }
  }, [searchParams, location.state, navigate]);

  // --- Suggestions: hit /api/keywords for 1+ chars
  useEffect(() => {
    const q = query.trim();
    setActiveIdx(-1);

    // nothing typed -> hide
    if (!q) {
      setSuggestions([]);
      setShowSuggest(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      let pool = [];

      if (q.length >= 1) {
        try {
          const res = await fetch(`/api/keywords?q=${encodeURIComponent(q)}&limit=8`);
          if (res.ok) {
            const { data } = await res.json();
            if (Array.isArray(data)) {
              // dedupe by name (case-insensitive)
              const seen = new Set();
              const deduped = [];
              for (const item of data) {
                const nLower = (item.name || '').trim().toLowerCase();
                if (!nLower || seen.has(nLower)) continue;
                seen.add(nLower);
                deduped.push({ keywordID: item.keywordID || item.name, name: item.name });
              }
              pool = deduped;
            }
          }
        } catch {
          // ignore network errors for suggestions
        }
      }

      pool = pool.slice(0, 8);
      setSuggestions(pool);
      setShowSuggest(pool.length > 0);
    }, 300); // smooth & snappy

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close suggestions on outside click
  useEffect(() => {
    const onClickAway = (e) => {
      if (!dropdownRef.current || !inputRef.current) return;
      if (
        !dropdownRef.current.contains(e.target) &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggest(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidZip(zipCode)) {
      setZipError('Please enter a valid U.S. ZIP code');
      return;
    }
    if (!query.trim()) return;
    setZipError('');
    setShowSuggest(false);
    navigate(`/results?zip=${zipCode}&q=${encodeURIComponent(query)}`, { state: { zipCode, userQuery: query } });
  };

  // keyboard navigation on the input
  const onInputKeyDown = (e) => {
    if (!showSuggest || !suggestions.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        e.preventDefault();
        const chosen = suggestions[activeIdx];
        setQuery(chosen.name);
        setShowSuggest(false);
      }
    } else if (e.key === 'Escape') {
      setShowSuggest(false);
    }
  };

  // simple inline styles to give a clean, professional dropdown
  const ddStyles = {
    container: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 6,
      background: '#ffffff', // solid white panel
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      zIndex: 1000,
      maxHeight: 280,
      overflowY: 'auto',
    },
    item: (active) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '12px 14px',
      fontSize: 14,
      lineHeight: '20px',
      cursor: 'pointer',
      background: active ? '#f3f4f6' : '#ffffff',
      borderBottom: '1px solid #f5f5f5',
      color: '#111827', // force dark text
    }),
    itemText: {
      flex: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: '#111827',
    },
  };

  return (
    <>
      <Header />

      {/* HERO with background image + overlay */}
      <section
        className="hero"
        style={{ backgroundImage: "url(/image.png)" }}   // <-- from public/
      >
        <div className="hero__overlay" />
        <div className="hero__content">
          <h1 className="hero__title">
            Get Your Home Project Done <br className="hide-on-mobile" /> with the Help of <span className="accent">Trusted Professionals</span>
          </h1>

          <p className="hero__subtitle"></p>

          {/* White card with the form */}
          <div className="card">
            <form onSubmit={handleSubmit} aria-label="Find service professionals">
              {/* ZIP */}
              <div className="form-group">
                <label className="label" htmlFor="zip">ZIP Code</label>
                <input
                  id="zip"
                  inputMode="numeric"
                  pattern="\d{5}"
                  aria-invalid={!!zipError}
                  aria-describedby={zipError ? "zip-error" : undefined}
                  type="text"
                  value={zipCode}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "").slice(0, 5);
                    setZipCode(raw);
                    if (zipError && isValidZip(raw)) setZipError('');
                  }}
                  className={`input ${zipError ? 'input--invalid' : ''}`}
                  placeholder="Enter your ZIP code"
                  maxLength={5}
                  required
                />
                {zipError && <p id="zip-error" className="error">{zipError}</p>}
              </div>

              {/* userQuery free-text + suggestions */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="label" htmlFor="query">Service Type</label>
                <input
                  id="query"
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => {
                    if (suggestions.length) setShowSuggest(true);
                  }}
                  onKeyDown={onInputKeyDown}
                  className="input"
                  placeholder="e.g., TV Mounting, Pest Control, Roof Repair…"
                  required
                  autoComplete="off"
                />

                {showSuggest && suggestions.length > 0 && (
                  <div ref={dropdownRef} style={ddStyles.container} role="listbox" aria-label="Suggestions">
                    {suggestions.map((s, i) => (
                      <div
                        key={`${s.keywordID}-${i}`}
                        role="option"
                        aria-selected={activeIdx === i}
                        style={ddStyles.item(activeIdx === i)}
                        onMouseEnter={() => setActiveIdx(i)}
                        onMouseDown={(e) => { e.preventDefault(); }}
                        onClick={() => {
                          setQuery(s.name || '');
                          setShowSuggest(false);
                          inputRef.current?.focus();
                        }}
                      >
                        <span style={ddStyles.itemText}>{s.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button type="submit" className="button">
                Find Pros in My Area
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default LandingPage;
